import { CaretDownFilled, CaretUpFilled, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Input, Radio, Select, Typography } from 'antd';

import { APIClient } from '../api/useAPIClient';
import { JobPostingDetail, PaginatedResponse, QuestionCollection, SerialID } from '../types';
import Form from './Form';

export interface AdditionalQuestionsFormValues {
  questions:
    | {
        id?: SerialID;
        type: QuestionCollection['type'];
        options?: {
          description: string;
          id?: SerialID;
        }[];
        description: string;
        required?: boolean;
      }[]
    | undefined;
}

export const additionalQuestionsFormMutation = async (
  jobPostingId: JobPostingDetail['id'],
  questions: AdditionalQuestionsFormValues['questions'],
  api: APIClient
) => {
  let questionsPage: PaginatedResponse<QuestionCollection> = {
    count: 0,
    next: undefined,
    results: [],
  };

  do {
    questionsPage = (
      await (questionsPage?.next
        ? api.get(questionsPage.next)
        : api.get('/questions/', {
            params: { job_posting_id: jobPostingId },
          }))
    ).data as PaginatedResponse<QuestionCollection>;

    for (const record of questionsPage.results) {
      for (const recordAlternative of record.alternatives || []) {
        if (
          !questions?.some(
            (question) =>
              ['CHECKBOX', 'RADIO'].includes(question.type) &&
              question.options?.some((alternative) => alternative.id === recordAlternative.id)
          )
        ) {
          await api.delete(`/question-alternatives/${recordAlternative.id}/`);
        }
      }

      if (!questions?.some((question) => question.id === record.id)) {
        await api.delete(`/questions/${record.id}/`);
      }
    }
  } while (questionsPage.next);

  if (questions && questions.length > 0) {
    for (let idx = 0; idx < questions.length; idx++) {
      const question = questions[idx];
      let questionId = question.id;

      if (question.id) {
        await api.patch(`/questions/${question.id}/`, {
          description: question.description,
          type: question.type,
          required: question.required,
          sequence: idx,
        });
      } else {
        const response = await api.post('/questions/', {
          job_posting: jobPostingId,
          description: question.description,
          type: question.type,
          required: question.required,
          sequence: idx,
        });

        questionId = response.data.id;
      }

      if (['CHECKBOX', 'RADIO'].includes(question.type) && question.options) {
        for (let idx = 0; idx < question.options.length; idx++) {
          const alternative = question.options[idx];

          if (alternative.id) {
            await api.patch(`/question-alternatives/${alternative.id}/`, {
              description: alternative.description,
              sequence: idx,
            });
          } else {
            await api.post('/question-alternatives/', {
              question: questionId,
              description: alternative.description,
              sequence: idx,
            });
          }
        }
      }
    }
  }
};

interface AdditionalQuestionsFormProps {
  disabled?: boolean;
  disabledInfo?: boolean;
  namePrefix?: (string | number)[];
  aiAction?: React.ReactElement;
}

const AdditionalQuestionsForm = ({
  disabled,
  namePrefix = [],
  disabledInfo,
  aiAction,
}: AdditionalQuestionsFormProps) => {
  const form = Form.useFormInstance();
  const questionsValue = Form.useWatch([0, 'questions'], form);

  return (
    <>
      {disabledInfo && (
        <>
          <Alert
            message="Não é possível editar as perguntas de pré-requisito, pois já existem candidatos no processo seletivo dessa vaga"
            type="info"
            showIcon
          />
          <br />
        </>
      )}
      <Typography.Paragraph>
        Faça até 5 perguntas de pré-requisito para validar os requisitos mínimos da vaga. A pessoa
        candidata irá respondê-las após o preenchimento do formulário de inscrição:
      </Typography.Paragraph>
      {aiAction}
      <Form.List name={[...namePrefix, 'questions']}>
        {(fields, { add, remove, move }) => (
          <>
            {fields.map(({ key, name }, idx) => (
              <div key={key}>
                <Form.Row>
                  <Form.Col flex={1}>
                    <Form.Item
                      name={[name, 'description']}
                      label={
                        <>
                          <Typography.Text>
                            Pergunta {name + 1}{' '}
                            <Button
                              type="text"
                              disabled={disabled}
                              size="small"
                              title={`Mover Pergunta ${name + 1} para cima`}
                              onClick={() => move(idx, idx - 1)}
                            >
                              <CaretUpFilled />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              disabled={disabled}
                              title={`Mover Pergunta ${name + 1} para baixo`}
                              onClick={() => move(idx, idx + 1)}
                            >
                              <CaretDownFilled />
                            </Button>
                          </Typography.Text>
                        </>
                      }
                      rules={[
                        { required: true },
                        {
                          validator(_, value) {
                            if (!value || value.length <= 255) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error('A pergunta não pode ultrapassar 255 caracteres')
                            );
                          },
                        },
                      ]}
                    >
                      <Input disabled={disabled} />
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={5}>
                    <Form.Item
                      name={[name, 'type']}
                      label="Tipo de pergunta"
                      required
                      rules={[{ required: true }]}
                    >
                      <Select disabled={disabled}>
                        <Select.Option value="CHECKBOX">Múltipla escolha</Select.Option>
                        <Select.Option value="RADIO">Única escolha</Select.Option>
                        <Select.Option value="TEXT">Texto livre</Select.Option>
                      </Select>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col>
                    <Form.Item name={[name, 'required']} valuePropName="checked" label={<></>}>
                      <Checkbox disabled={disabled}>Obrigatório</Checkbox>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col>
                    <Form.Item label={<></>}>
                      <Button
                        disabled={disabled}
                        size="small"
                        type="text"
                        title={`Remover Pergunta ${name + 1}`}
                        onClick={() => remove(name)}
                      >
                        <DeleteOutlined />
                      </Button>
                    </Form.Item>
                  </Form.Col>
                </Form.Row>
                <>
                  {['RADIO', 'CHECKBOX'].includes(questionsValue?.[idx]?.type ?? '') && (
                    <Form.List
                      name={[name, 'options']}
                      rules={[
                        {
                          validator: (_, value) =>
                            value && value.length > 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error('A pergunta precisa ter no mínimo uma opção.')
                                ),
                        },
                      ]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <Form.Item>
                          {fields.map(({ key, name: optionName }) => (
                            <Form.Row key={`options${key}`}>
                              <Form.Col span={1}>
                                <Form.Item>
                                  {questionsValue?.[idx]?.type === 'RADIO' && (
                                    <Radio disabled={disabled} checked={false} />
                                  )}
                                  {questionsValue?.[idx]?.type === 'CHECKBOX' && (
                                    <Checkbox disabled={disabled} checked={false} />
                                  )}
                                </Form.Item>
                              </Form.Col>
                              <Form.Col span={12}>
                                <Form.Item
                                  name={[optionName, 'description']}
                                  rules={[
                                    { required: true },
                                    {
                                      validator(_, value) {
                                        if (value.length <= 255) {
                                          return Promise.resolve();
                                        }
                                        return Promise.reject(
                                          new Error(
                                            'A alternativa não pode ultrapassar 255 caracteres'
                                          )
                                        );
                                      },
                                    },
                                  ]}
                                >
                                  <Input disabled={disabled} />
                                </Form.Item>
                              </Form.Col>
                              <Form.Col>
                                <Form.Item>
                                  <Button
                                    size="small"
                                    disabled={disabled}
                                    type="text"
                                    onClick={() => remove(optionName)}
                                  >
                                    <DeleteOutlined />
                                  </Button>
                                </Form.Item>
                              </Form.Col>
                            </Form.Row>
                          ))}
                          <Form.ErrorList errors={errors} />
                          <Button type="link" disabled={disabled} onClick={() => add()}>
                            Adicionar opção
                          </Button>
                        </Form.Item>
                      )}
                    </Form.List>
                  )}
                </>
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              disabled={fields.length >= 5 || disabled}
              onClick={() => add({ description: '', type: 'TEXT', required: false, options: [] })}
            >
              Adicionar pergunta
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
};

export default AdditionalQuestionsForm;
